import { useQuery } from '@apollo/client';
import type { DocumentReference } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Badge,
  Button,
  ColDefBuilder,
  DataTable,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Stack,
  Text,
  makeDefaultSortableColumnDef,
  toast,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { PATIENT_BY_IDENTIFIER_QUERY, useMedplum } from '@eluve/medplum-client';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

import { AgentIdentity, ExtractConsentModal } from './ExtractConsentModal';
import { ReviewDocumentModal } from './ReviewDocumentModal';
import { useTabActionsSlot } from './TabActionsSlotContext';

// meta.tag system stamped on every agent-authored resource, and the identifier
// system tying an extracted DiagnosticReport back to its source upload (see
// agents/extract-document.ts). Together they tell us which documents already
// have a lab report, so the row action can flip from "Extract" to a view link.
const AGENT_TAG_SYSTEM = 'https://eluve.com/agent';
const EXTRACTED_FROM_SYSTEM = 'https://eluve.com/extracted-from';

/** Review state of a document's extracted report, derived from report.status. */
type ReviewStatus = 'needs-review' | 'approved';

interface FileRow {
  id: string;
  name: string;
  contentType: string;
  /**
   * True if Medplum returned an attachment URL on the initial read. The URL
   * itself is signed and time-bounded, so we re-fetch the DocumentReference
   * on click to get a fresh URL instead of caching the stale one here.
   */
  hasAttachment: boolean;
  size: number | null;
  createdAt: string | null;
}

const toFileRow = (doc: DocumentReference, untitledLabel: string): FileRow => {
  const attachment = doc.content?.[0]?.attachment;
  return {
    id: doc.id ?? '',
    name: attachment?.title ?? doc.description ?? untitledLabel,
    contentType: attachment?.contentType ?? 'application/octet-stream',
    hasAttachment: Boolean(attachment?.url),
    size: attachment?.size ?? null,
    createdAt: doc.date ?? doc.meta?.lastUpdated ?? null,
  };
};

const formatBytes = (bytes: number | null): string => {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const PatientFiles: React.FC = () => {
  const { t } = useTranslation();
  const { patientId } = useParams() as { patientId: string };
  const { client, fhirClient } = useMedplum();
  const apiClient = useApiClient();
  const tenantId = useAssignedTenantIdFromParams();
  // Tab bar slot to portal the "Upload file" action into, so it sits beside the
  // tabs as this tab's action (mirrors the Observations toggle).
  const actionsSlot = useTabActionsSlot();

  const { data: patientData, loading: isPatientLoading } = useQuery(
    PATIENT_BY_IDENTIFIER_QUERY,
    {
      client: fhirClient ?? undefined,
      variables: { identifier: patientId },
      skip: !fhirClient,
      fetchPolicy: 'cache-and-network',
    },
  );

  const fhirPatientId = patientData?.PatientList?.[0]?.id ?? null;
  const hasAccess = Boolean(fhirPatientId);

  const [rows, setRows] = useState<FileRow[]>([]);
  // DocumentReference id → review status of its extracted report. Absent means
  // the document has not been extracted yet. Drives both the Status column and
  // the Extract-vs-View-Details action.
  const [reviewStatusByDoc, setReviewStatusByDoc] = useState<
    Map<string, ReviewStatus>
  >(() => new Map());
  // Source-document id whose review modal is open, or null.
  const [detailsDocId, setDetailsDocId] = useState<string | null>(null);
  // File being previewed inline (fresh signed URL), or null when closed.
  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    contentType: string;
  } | null>(null);
  // DocumentReference id whose preview URL is being resolved, for a spinner.
  const [openingPreviewId, setOpeningPreviewId] = useState<string | null>(null);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Extract agent state
  const AGENT_ID = 'extract-document';
  const [consentOpen, setConsentOpen] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [pendingDocRefId, setPendingDocRefId] = useState<string | null>(null);
  const [runningDocRefId, setRunningDocRefId] = useState<string | null>(null);
  const [scopes, setScopes] = useState<string[]>([]);
  const [agentIdentity, setAgentIdentity] = useState<AgentIdentity | null>(
    null,
  );

  useEffect(() => {
    if (!client || !fhirPatientId) return;
    let cancelled = false;
    setIsLoadingFiles(true);
    (async () => {
      try {
        const docs = await client.searchResources('DocumentReference', {
          subject: `Patient/${fhirPatientId}`,
          _count: 100,
          _sort: '-_lastUpdated',
        });
        if (cancelled) return;
        setRows(
          docs.map((doc) => toFileRow(doc, t('patients.files.untitled'))),
        );

        // Best-effort: map each document to the review status of its agent
        // DiagnosticReport, so the row can show a Status badge + a View Details
        // action. A permission gap on DiagnosticReport must not break the file
        // list, so this is isolated from the doc load above.
        try {
          const reports = await client.searchResources('DiagnosticReport', {
            subject: `Patient/${fhirPatientId}`,
            _count: 200,
          });
          if (cancelled) return;
          const byDoc = new Map<string, ReviewStatus>();
          for (const report of reports) {
            if (report.status === 'entered-in-error') continue;
            if (!report.meta?.tag?.some((t) => t.system === AGENT_TAG_SYSTEM)) {
              continue;
            }
            const docId = report.identifier?.find(
              (i) => i.system === EXTRACTED_FROM_SYSTEM,
            )?.value;
            if (!docId) continue;
            // preliminary = the human still needs to review/approve; any
            // attested status (final/amended/corrected) = approved. A
            // still-open draft wins over an approved one for the same doc.
            const status: ReviewStatus =
              report.status === 'preliminary' ? 'needs-review' : 'approved';
            if (status === 'needs-review' || !byDoc.has(docId)) {
              byDoc.set(docId, status);
            }
          }
          setReviewStatusByDoc(byDoc);
        } catch {
          // Leave the map as-is; rows just keep showing "Extract".
        }
      } catch {
        if (!cancelled) {
          setRows([]);
          toast.error(t('patients.files.loadFailed'));
        }
      } finally {
        if (!cancelled) setIsLoadingFiles(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, fhirPatientId, reloadKey, t]);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!client || !fhirPatientId) return;
      setIsUploading(true);
      try {
        // Two-step instead of client.createDocumentReference: the SDK helper
        // auto-sets securityContext on the Binary upload, which makes it send
        // an X-Security-Context header. Medplum's CORS preflight allowlist
        // does not currently include that header, so the upload fails from
        // the browser. Posting the Binary without securityContext sidesteps
        // it.
        const binary = await client.createBinary({
          data: file,
          contentType: file.type || 'application/octet-stream',
          filename: file.name,
        });
        // Stamp the uploader as author. The tenant-member AccessPolicy scopes
        // DocumentReference reads to `author=%profile`, so an authorless upload
        // would immediately fall out of the clinician's own read scope — the
        // row vanishes from the Files tab and the extraction agent can't read
        // it. `getActiveLogin().profile` is the same Practitioner reference the
        // policy resolves `%profile` to.
        const author = client.getActiveLogin()?.profile;
        await client.createResource({
          resourceType: 'DocumentReference',
          status: 'current',
          subject: { reference: `Patient/${fhirPatientId}` },
          ...(author ? { author: [author] } : {}),
          description: file.name,
          date: new Date().toISOString(),
          content: [
            {
              attachment: {
                contentType: file.type || 'application/octet-stream',
                url: `Binary/${binary.id}`,
                title: file.name,
                size: file.size,
              },
            },
          ],
        });
        toast.success(t('patients.files.uploaded', { name: file.name }));
        setReloadKey((k) => k + 1);
      } catch {
        toast.error(t('patients.files.uploadFailed', { name: file.name }));
      } finally {
        setIsUploading(false);
      }
    },
    [client, fhirPatientId, t],
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
    e.target.value = '';
  };

  const runExtract = useCallback(
    async (documentReferenceId: string) => {
      setRunningDocRefId(documentReferenceId);
      try {
        const res = await apiClient.miniApps.run({
          params: { tenantId, agentId: AGENT_ID },
          body: { documentReferenceId },
        });
        if (res.status === 200) {
          const { outcome, observationIds, factCount } = res.body;
          if (outcome === 'extracted') {
            // factCount = lab facts the model found in the document;
            // observationIds = the ones that survived the fact → Observation
            // conversion. A shortfall means some results were dropped, so warn
            // rather than report a clean success the reviewer would trust.
            const dropped = factCount - observationIds.length;
            if (dropped > 0) {
              toast.warning(
                t(
                  dropped === 1
                    ? 'patients.files.extractedPartialOne'
                    : 'patients.files.extractedPartialMany',
                  {
                    extracted: observationIds.length,
                    total: factCount,
                    dropped,
                  },
                ),
              );
            } else {
              toast.success(
                t(
                  observationIds.length === 1
                    ? 'patients.files.extractedSuccessOne'
                    : 'patients.files.extractedSuccessMany',
                  { count: observationIds.length },
                ),
              );
            }
            // The agent wrote server-side (a different client), so drop this
            // client's stale cached searches before reloadKey re-queries — else
            // the Status column stays empty until a hard refresh.
            client?.invalidateSearches('DiagnosticReport');
            client?.invalidateSearches('Observation');
            setReloadKey((k) => k + 1);
          } else if (outcome === 'unsupported') {
            toast.error(t('patients.files.notLabReport'));
          } else if (outcome === 'empty') {
            toast.warning(t('patients.files.noLabResults'));
          } else if (outcome === 'blocked') {
            toast.error(t('patients.files.reextractionDisabled'));
          }
        } else if (res.status === 409) {
          // A run for this same document is already in flight (double-click /
          // two tabs). Not a failure — just ask them to wait.
          toast.warning(t('patients.files.extractionAlreadyRunning'));
        } else {
          // Includes 403 (grant revoked) — user re-clicking Extract will
          // re-check the grant status and open the consent modal.
          const errMsg =
            res.body && typeof res.body === 'object' && 'error' in res.body
              ? String((res.body as { error: unknown }).error)
              : t('patients.common.unknownError');
          toast.error(
            t('patients.files.extractionFailedWithError', { error: errMsg }),
          );
        }
      } catch {
        toast.error(t('patients.files.extractionFailed'));
      } finally {
        setRunningDocRefId(null);
      }
    },
    [apiClient, tenantId, client, t],
  );

  const openConsentForDoc = useCallback(
    async (documentReferenceId: string) => {
      try {
        const res = await apiClient.miniApps.grant({
          params: { tenantId, agentId: AGENT_ID },
        });
        if (res.status === 200) {
          setScopes(res.body.scopesPlainEnglish);
          setAgentIdentity(res.body.agent);
        }
      } catch {
        // Non-fatal — modal will still open with empty scope list / no agent
        // identity (a graceful fallback renders generic copy).
      }
      setPendingDocRefId(documentReferenceId);
      setConsentOpen(true);
    },
    [apiClient, tenantId],
  );

  const handleExtractClick = useCallback(
    async (documentReferenceId: string) => {
      // Check grant status first; only show consent if no active grant.
      try {
        const res = await apiClient.miniApps.grant({
          params: { tenantId, agentId: AGENT_ID },
        });
        if (res.status === 200 && res.body.granted) {
          await runExtract(documentReferenceId);
        } else {
          await openConsentForDoc(documentReferenceId);
        }
      } catch {
        // If the grant lookup itself fails, prompt for consent — install
        // will surface the real error.
        await openConsentForDoc(documentReferenceId);
      }
    },
    [apiClient, tenantId, runExtract, openConsentForDoc],
  );

  const handleAllowConsent = useCallback(async () => {
    if (!pendingDocRefId) return;
    setIsInstalling(true);
    try {
      const installRes = await apiClient.miniApps.install({
        params: { tenantId, agentId: AGENT_ID },
        body: {},
      });
      if (installRes.status !== 200) {
        const errMsg =
          installRes.body &&
          typeof installRes.body === 'object' &&
          'error' in installRes.body
            ? String((installRes.body as { error: unknown }).error)
            : t('patients.common.unknownError');
        toast.error(
          t('patients.files.installFailedWithError', { error: errMsg }),
        );
        return;
      }
      setConsentOpen(false);
      await runExtract(pendingDocRefId);
    } catch {
      toast.error(t('patients.files.installFailed'));
    } finally {
      setIsInstalling(false);
      setPendingDocRefId(null);
    }
  }, [apiClient, tenantId, pendingDocRefId, runExtract, t]);

  // Fetch an attachment's bytes through the authenticated FHIR Binary endpoint
  // and hand back a blob URL an <iframe>/<img> can render. A fresh
  // DocumentReference read can rewrite the attachment to a bare `/storage/...`
  // path with no presigned signature (this self-hosted Medplum), which 401s in
  // an iframe — so download the `Binary/<id>` reference instead (the same path
  // the extraction backend uses), deriving the id from the storage path.
  const attachmentBlobUrl = useCallback(
    async (attachmentUrl: string): Promise<string | null> => {
      if (!client) return null;
      const storageId = attachmentUrl.match(/\/storage\/([^/?]+)/)?.[1];
      const ref = attachmentUrl.startsWith('Binary/')
        ? attachmentUrl
        : storageId
          ? `Binary/${storageId}`
          : attachmentUrl;
      const blob = await client.download(ref);
      return URL.createObjectURL(blob);
    },
    [client],
  );

  const handleOpen = useCallback(
    async (e: React.MouseEvent, row: FileRow) => {
      e.preventDefault();
      if (!client) return;
      // Re-read the DocumentReference for a fresh attachment URL: Medplum's
      // storage URLs are time-bounded, so the one read on page load may have
      // expired. Then show it inline in a preview modal rather than a new tab.
      setOpeningPreviewId(row.id);
      try {
        const fresh = await client.readResource('DocumentReference', row.id);
        const attachment = fresh.content?.[0]?.attachment;
        const url = attachment?.url;
        if (url) {
          const objectUrl = await attachmentBlobUrl(url);
          if (objectUrl) {
            setPreview({
              url: objectUrl,
              name: row.name,
              contentType: attachment?.contentType ?? row.contentType,
            });
          } else {
            toast.error(t('patients.files.noDownloadUrl'));
          }
        } else {
          toast.error(t('patients.files.noDownloadUrl'));
        }
      } catch {
        toast.error(t('patients.files.openFailed', { name: row.name }));
      } finally {
        setOpeningPreviewId(null);
      }
    },
    [client, attachmentBlobUrl, t],
  );

  // Open the side-by-side review modal for a document. The modal resolves and
  // renders the source file on the left itself.
  const openDetails = useCallback((row: FileRow) => {
    setDetailsDocId(row.id);
  }, []);

  const columns = useMemo(
    () =>
      new ColDefBuilder<FileRow>()
        .colDef({
          ...makeDefaultSortableColumnDef('name', t('patients.files.name')),
          id: 'name',
          // Stretch Name to absorb slack so every other column shrinks to its
          // content and sits flush to the right.
          meta: { cellClassName: 'w-full', headerClassName: 'w-full' },
          cell: ({ row }) =>
            row.original.hasAttachment ? (
              <a
                href="#"
                onClick={(e) => handleOpen(e, row.original)}
                className="cursor-pointer text-left hover:underline aria-busy:opacity-60"
                aria-busy={openingPreviewId === row.original.id}
              >
                {row.original.name}
                {openingPreviewId === row.original.id ? ' …' : ''}
              </a>
            ) : (
              <span className="text-text-tertiary">{row.original.name}</span>
            ),
        })
        .defaultSortable('contentType', t('patients.files.type'))
        .colDef({
          id: 'size',
          header: t('patients.files.size'),
          accessorKey: 'size',
          cell: ({ row }) => formatBytes(row.original.size),
        })
        .dateSortable('createdAt', t('patients.files.uploadedColumn'))
        .colDef({
          id: 'reviewStatus',
          header: t('patients.files.status'),
          cell: ({ row }) => {
            if (!row.original.hasAttachment) return null;
            const status = reviewStatusByDoc.get(row.original.id);
            if (!status) {
              return (
                <Text size="sm" color="tertiary">
                  {t('patients.files.notExtracted')}
                </Text>
              );
            }
            return status === 'needs-review' ? (
              <Badge variant="default">
                {t('patients.extracted.needsReview')}
              </Badge>
            ) : (
              <Badge variant="success">
                {t('patients.extracted.approved')}
              </Badge>
            );
          },
        })
        .colDef({
          id: 'extract',
          header: '',
          cell: ({ row }) => {
            if (!row.original.hasAttachment) return null;
            // Once this document has agent-extracted entities, the action opens
            // a details modal rather than re-running the extraction.
            return (
              <div className="flex justify-end">
                {reviewStatusByDoc.has(row.original.id) ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDetails(row.original)}
                  >
                    {t('patients.files.viewDetails')}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExtractClick(row.original.id)}
                    loading={runningDocRefId === row.original.id}
                    disabled={runningDocRefId !== null}
                  >
                    {t('patients.files.extract')}
                  </Button>
                )}
              </div>
            );
          },
        })
        .build(),
    [
      handleOpen,
      handleExtractClick,
      openDetails,
      runningDocRefId,
      reviewStatusByDoc,
      openingPreviewId,
      t,
    ],
  );

  if (isPatientLoading && !patientData) {
    return <Text>{t('patients.common.checkingAccess')}</Text>;
  }

  if (!hasAccess) {
    return (
      <Stack gap={3}>
        <div className="rounded-md border border-dashed p-4">
          <Text weight="medium">
            {t('patients.medicalProfile.noFhirPatientTitle')}
          </Text>
          <Text size="sm" color="tertiary">
            {t('patients.medicalProfile.noFhirPatientDescription')}
          </Text>
        </div>
      </Stack>
    );
  }

  return (
    <Stack gap={3}>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={handleFileInputChange}
      />
      {actionsSlot &&
        createPortal(
          <Button
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            loading={isUploading}
            disabled={isUploading}
          >
            {t('patients.files.uploadFile')}
          </Button>,
          actionsSlot,
        )}
      {isLoadingFiles && rows.length === 0 ? (
        <Text color="tertiary">{t('patients.common.loading')}</Text>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          initialSortingState={[{ id: 'createdAt', desc: true }]}
        />
      )}
      <ExtractConsentModal
        open={consentOpen}
        onOpenChange={(open) => {
          setConsentOpen(open);
          if (!open) setPendingDocRefId(null);
        }}
        agent={agentIdentity}
        scopesPlainEnglish={scopes}
        isInstalling={isInstalling}
        onAllow={handleAllowConsent}
      />
      <Dialog
        open={preview !== null}
        onOpenChange={(open) => {
          if (!open) {
            if (preview) URL.revokeObjectURL(preview.url);
            setPreview(null);
          }
        }}
      >
        <DialogContent size="4xl" className="max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">{preview?.name}</DialogTitle>
          </DialogHeader>
          {preview &&
            (preview.contentType.startsWith('image/') ? (
              <img
                src={preview.url}
                alt={preview.name}
                className="max-h-[75vh] w-full rounded-md object-contain"
              />
            ) : (
              <iframe
                src={preview.url}
                title={preview.name}
                className="h-[75vh] w-full rounded-md border"
              />
            ))}
        </DialogContent>
      </Dialog>
      {client && fhirPatientId && (
        <ReviewDocumentModal
          client={client}
          fhirPatientId={fhirPatientId}
          documentReferenceId={detailsDocId}
          onOpenChange={(open) => {
            if (!open) {
              setDetailsDocId(null);
              // The user may have approved/rejected inside the modal; refresh
              // so the Status column reflects the new report state.
              setReloadKey((k) => k + 1);
            }
          }}
        />
      )}
    </Stack>
  );
};
